import { type PaletteKey, type PixelCanvas, sprite } from '../../../lib';
import { box, byRow, dots, geometry, outlined, round, taper } from '../effect-guns.sprite';

/**
 * Gloop Gun (the Ooze set's gun, ADR-0160 amended): a tank blaster of living goo. A slate stock in rubber ridges, a riveted
 * steel body with a window down its side where goo runs through, a glass jar standing on its back full of bubbling
 * magenta goo (its level bobbing, bubbles rising through it, a white glint down the glass), a ringed steel barrel to a
 * nozzle caked in goo, a drop hanging off it. On a shot (`fire`) the goo in the jar slops down and splashes up, the window
 * and the nozzle flash pink, then it settles.
 */

const STEEL = ['2', '3', '4', '5'] as const;
const GOO = ['P', 'p', 'i', 'w'] as const;

/** The jar on the body's back: glass x 27..39, y 2..13, its goo's top at `level`, bubbles [x, y] in it. */
function jar(c: PixelCanvas, level: number, bubbles: readonly (readonly [number, number])[], splash: boolean): void {
  // The lid: a steel cap with a lit top.
  box(c, 28, 1, 38, 2, [], (_x, y) => (y === 1 ? '5' : '3'));
  for (let y = 3; y <= 13; y++)
    for (let x = 27; x <= 39; x++) {
      const glass = x === 27 || x === 39;
      if (glass) {
        c.set(x, y, x === 27 ? '5' : '3');
        continue;
      }
      if (y < level) {
        // Above the goo: dark glass, a glint down its left.
        c.set(x, y, x === 29 ? '4' : '1');
        continue;
      }
      const top = y === level;
      c.set(x, y, top ? 'i' : x === 29 ? 'i' : x >= 37 ? 'P' : 'p');
    }
  for (const [x, y] of bubbles) if (y >= level) c.set(x, y, 'w').set(x + 1, y, 'i');
  if (splash)
    dots(c, 'i', [
      [30, level - 2],
      [33, level - 3],
      [36, level - 2],
    ]);
  // Its foot: a steel collar on the body.
  box(c, 26, 13, 40, 14, [], (_x, y) => (y === 13 ? '4' : '2'));
}

const LEVELS = [5, 4, 5, 6] as const;
const RISE = [
  [
    [31, 11],
    [35, 8],
  ],
  [
    [31, 9],
    [35, 12],
  ],
  [
    [31, 7],
    [35, 10],
  ],
  [
    [30, 12],
    [35, 8],
  ],
] as const;

const gloop = (frame: number, heat = 0) =>
  outlined((c: PixelCanvas) => {
    // The stock: slate rubber in ridges.
    box(c, 5, 16, 22, 25, [2, 1], (x, y) => (y === 16 ? '3' : y >= 24 ? '1' : x % 3 === 0 ? '1' : '2'), 'l');
    jar(c, heat >= 2 ? 9 : heat === 1 ? 7 : (LEVELS[frame % 4] as number), RISE[frame % 4] ?? [], heat >= 2);
    // The body: riveted steel, lit on top.
    box(c, 21, 14, 46, 27, [2, 1], byRow(14, 27, STEEL, 1, 2), 'r');
    dots(c, '5', [
      [23, 16],
      [44, 16],
      [23, 25],
    ]);
    // The window down its side, goo running through it.
    for (let x = 24; x <= 43; x++) {
      c.set(x, 19, '1');
      const flow = (x + frame * 2) % 5;
      c.set(x, 20, heat ? 'i' : flow === 0 ? 'i' : 'p');
      c.set(x, 21, heat ? 'p' : flow === 3 ? 'i' : 'P');
      c.set(x, 22, '5');
    }
    // The trigger.
    box(c, 30, 28, 33, 30, [0, 1], (_x, y): PaletteKey => (y === 28 ? '3' : '2'), 'r');
    // The ringed barrel, a nozzle caked in goo, a drop hanging off it.
    const steel = round(STEEL);
    taper(c, 47, 55, 19.5, 3.6, 3, (top, bottom, x) => (x % 3 === 0 ? '2' : steel(top, bottom)));
    taper(c, 56, 62, 19.5, 3, 1.6, heat ? (top) => (top === 0 ? 'w' : 'i') : round(GOO));
    dots(c, 'p', [
      [57, 23],
      [59, 23],
    ]);
    dots(c, heat ? 'i' : 'P', [[59, 24]]);
  });

export default sprite({
  name: 'pollinator_ooze',
  ...geometry,
  notes: `Gloop Gun (ADR-0160 amended, the Ooze set's gun): a tank blaster of living goo. A slate stock in rubber ridges, a
riveted steel body with a window down its side where magenta goo runs through (2 px a frame), a glass jar standing on its
back full of bubbling goo (its level bobbing over 4 frames, two bubbles rising through it, a glint down the glass), a
ringed steel barrel to a nozzle caked in goo, a drop hanging off it. Fire: the goo slops down and splashes up in the jar,
the window and the nozzle flash pink, settling over 3 frames. Its flair: the jar glowing, bubbles out of its lid, drops
off the nozzle. No team keys. Rubric (4×): S4 R4 P4 L4 C4 A4 F4. Weakest: the jar is 13 px wide, at 1:1 a pink square on
a grey gun (its glow and its bubbles carry it).`,
  anims: {
    idle: { fps: 6, frames: [gloop(0), gloop(1), gloop(2), gloop(3)] },
    fire: { fps: 20, loop: false, frames: [gloop(0, 3), gloop(1, 2), gloop(2, 1), gloop(3)] },
  },
});
